import React, { useMemo } from "react";
import { useQuery } from '@apollo/client/react';
import '../Settings.scss';
import { ACTIVITY_COUNTS } from '../../../services/queries.js';
import activityDictionary from '../../../config/activityDicitionary.js';
import DataTable from './DataTable.jsx';

const ACTIVITY_COLS = [
    { label: 'Key',          key: 'id',
      fmt: v => <code className="drilldown-code">{v}</code> },
    { label: 'Display Name', key: 'displayName' },
    { label: 'Category',     key: 'category' },
    { label: 'Description',  key: 'description' },
    { label: 'Icon',         key: 'icon' },
    { label: 'Count',        key: 'count',
      fmt: v => typeof v === 'number' ? v.toLocaleString() : v },
];

const ORPHAN_COLS = [
    { label: 'Activity ID', key: 'id',
      fmt: v => <code className="drilldown-code">{v}</code> },
    { label: 'Count',       key: 'count', fmt: v => v.toLocaleString() },
];

const ActivityCounts = () => {
    const { data: queryData, loading, error } = useQuery(ACTIVITY_COUNTS, {
        fetchPolicy: 'cache-and-network',
    });

    const activityCountsMap = useMemo(() => {
        if (!queryData?.countByActivity) return {};

        return queryData.countByActivity.reduce((acc, item) => {
            acc[item.activity] = item.count;
            return acc;
        }, {});
    }, [queryData]);

    const activityRows = useMemo(() => (
        Object.values(activityDictionary).map(a => ({
            id:          a.id,
            displayName: a.displayName,
            category:    a.category,
            description: a.description,
            icon:        a.icon,
            count:       loading ? 'Loading...' : (activityCountsMap[a.id] || 0),
            inactive:    a.active === false,
        }))
    ), [activityCountsMap, loading]);

    const orphanActivities = useMemo(() => {
        if (!queryData?.countByActivity) return [];

        const dictIds = new Set(Object.values(activityDictionary).map(a => a.id));

        return queryData.countByActivity
            .filter(item => !dictIds.has(item.activity))
            .map(item => ({ id: item.activity, count: item.count }));
    }, [queryData]);

    if (error) return <div className="error">{error.message}</div>;

    return (
        <div className="settings-section">
            <h3>Activity Dictionary</h3>
            <p>Energy activity types with their display names and metadata</p>

            <DataTable
                cols={ACTIVITY_COLS}
                rows={activityRows}
                rowKey="id"
                rowStyle={row => row.inactive ? { color: '#adb5bd' } : undefined}
            />

            {orphanActivities.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <h4>Activities Not in Dictionary</h4>
                    <DataTable
                        cols={ORPHAN_COLS}
                        rows={orphanActivities}
                        rowKey="id"
                    />
                </div>
            )}
        </div>
    );
};

export default ActivityCounts;
